import useGlobalModal from 'shared/global-modal/libs/useGlobalModal'
import { useHandleRequest } from 'shared/libs/hooks/useHandleRequest/useHandleRequest'
import { cleanObject } from 'shared/libs/obj/cleanObjFromNull'
import { getPhoneNumberMask } from 'shared/libs/phoneNumber'

import { useNavigate } from 'react-router-dom'

import {
  useCreateUserMutation,
  useGetUserQuery,
  useResetPasswordMutation,
  useUpdateUserMutation,
} from 'modules/AccessManage/services/accessManageApi'
import {
  ChangeUserRequest,
  ResetPasswordFormState,
  UserInfoFormState,
} from 'modules/AccessManage/types'

interface Props {
  userId?: string
}
export const useUser = ({ userId }: Props) => {
  const navigate = useNavigate()
  const { closeModal } = useGlobalModal()
  const { handleSuccess, handleError } = useHandleRequest()

  const [createUser, { isLoading: isCreateUserLoading }] =
    useCreateUserMutation()
  const [updateUser, { isLoading: isUpdateUserLoading }] =
    useUpdateUserMutation()
  const [resetPassword, { isLoading: isResetPasswordLoading }] =
    useResetPasswordMutation()
  const { data: user, isFetching: isGetUserFetching } = useGetUserQuery(
    {
      params: {
        id: `${userId}`,
      },
    },
    {
      skip: !userId,
    },
  )

  const handleUpdateUser = async (body: Omit<ChangeUserRequest, 'params'>) => {
    try {
      const res = await updateUser({
        ...body,
        params: {
          id: `${userId}`,
        },
      }).unwrap()
      if (res.success) {
        handleSuccess('Изменения сохранены')
        const userInfoPage = location.pathname.replace('/edit-user', '')
        navigate(userInfoPage)
      }
    } catch (err) {
      handleError(err)
    }
  }

  const handleChangeIsBlockedUser = async (value: boolean) => {
    try {
      const res = await updateUser({
        is_blocked: value,
        params: {
          id: `${userId}`,
        },
      }).unwrap()
      if (res.success) {
        handleSuccess(
          value ? 'Пользователь заблокирован' : 'Пользовател разблокирован',
        )
        closeModal()
      }
    } catch (err) {
      handleError(err)
    }
  }

  const handleChangePassword = async (data: ResetPasswordFormState) => {
    try {
      const res = await resetPassword({
        ...data,
        params: {
          id: `${userId}`,
        },
      }).unwrap()
      if (res.success) {
        handleSuccess('Пароль успешно сброшен')
        closeModal()
      }
    } catch (err) {
      handleError(err)
    }
  }

  const handleCreateUser = async (data: UserInfoFormState) => {
    try {
      const res = await createUser(data).unwrap()
      if (res.success) {
        handleSuccess('Пользователь успешно создан')
        navigate('/dashboard/access-manage/')
      }
    } catch (err) {
      handleError(err)
    }
  }

  const cleanUserObject = (data: UserInfoFormState): UserInfoFormState => {
    const { phoneNumberCode } = getPhoneNumberMask('KG')
    return cleanObject({
      ...data,
      phone_number:
        data.phone_number !== phoneNumberCode ? data.phone_number : null,
      contact_phone_number:
        data.contact_phone_number !== phoneNumberCode
          ? data.contact_phone_number
          : null,
    }) as UserInfoFormState
  }

  return {
    user: user?.result,
    handleUpdateUser,
    handleChangeIsBlockedUser,
    handleChangePassword,
    handleCreateUser,
    cleanUserObject,
    isGetUserFetching,
    isCreateUserLoading,
    isUpdateUserLoading,
    isResetPasswordLoading,
  }
}
